<template>
    <v-layout column>
        <v-layout align-center>
            <v-toolbar class="text-h5 mb-4">
                <v-flex>
                    <v-icon class="mr-3">shopping_cart</v-icon>
                    Reservation Options
                </v-flex>
                <v-btn
                  data-testid="reset-reservation-lead-btn"
                  class="reset-reservation-lead-btn mr-2"
                  @click="handleResetForm"
                  color="red"
                  outlined
                >
                  Reset Form
                </v-btn>
                <facility-tenant-search v-if="!tenant" @tenantSelected="setTenant($event)">
                    <v-btn
                        data-testid="reservations-find-tenant-btn"
                        class="reservations-find-tenant-btn info"
                        outlined
                    >
                        <v-icon class="mr-3">search</v-icon>
                        Find Tenant
                    </v-btn>
                </facility-tenant-search>
                <v-btn
                    data-testid="reservation-auto-fill-btn"
                    class="reservation-auto-fill-btn px-3 info"
                    v-else
                    outlined
                    @click="setTenant(tenant)"
                >
                    <v-icon class="mr-3">transform</v-icon>
                    Auto-fill fields
                </v-btn>
            </v-toolbar>
        </v-layout>
        <v-form>
            <v-layout>
                <app-input
                    data-testid="reservation-first-name-input"
                    class="reservation-first-name-input px-2"
                    filled
                    label="First Name"
                    v-model="firstName"
                    :rules="'required'"
                ></app-input>
                <app-input
                    data-testid="reservation-last-name-input"
                    class="reservation-last-name-input px-2"
                    filled
                    label="Last Name"
                    v-model="lastName"
                    :rules="'required'"
                ></app-input>
            </v-layout>
            <v-layout>
                <app-input
                    data-testid="reservation-email-input"
                    class="reservation-email-input px-2"
                    filled
                    label="Email Address"
                    v-model="email"
                    type="email"
                    :rules="'required'"
                ></app-input>
                <app-input
                    data-testid="reservation-phone-input"
                    class="reservation-phone-input px-2"
                    filled
                    label="Phone Number"
                    v-model="phone"
                    type="phone"
                    :rules="'required'"
                ></app-input>
            </v-layout>
            <v-layout class="text-h6 mt-2 mb-4 ml-3 align-center">
                <span class="mr-3">Reservation Type:</span>
                <v-radio-group
                    class="mt-0 pt-0"
                    box
                    v-model="reservationType"
                    row
                    hide-details
                >
                    <v-radio
                      data-testid="reservation-select-reservation-radio"
                      class="reservation-select-reservation-radio"
                      value="reservation"
                    >
                        <template v-slot:label>
                            <div class="text-h6" :class="darkMode ? 'white--text' : 'black--text'">Reservation</div>
                        </template>
                    </v-radio>
                    <v-radio
                      data-testid="reservation-select-lead-radio"
                      class="reservation-select-lead-radio"
                      value="inquiry"
                    >
                        <template v-slot:label>
                            <div class="text-h6" :class="darkMode ? 'white--text' : 'black--text'">Lead</div>
                        </template>
                    </v-radio>
                </v-radio-group>
            </v-layout>
            <v-layout align-center>
                <v-flex xs6 class="pl-1 pr-2">
                    <v-menu
                        data-testid="reservation-move-in-menu"
                        class="reservation-move-in-menu"
                        ref="moveIn"
                        v-model="moveInSelect"
                        :close-on-content-click="false"
                        :return-value.sync="moveInDate"
                        :nudge-right="10"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                    >
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                data-testid="reservation-move-in-input"
                                class="reservation-move-in-input"
                                v-model="moveInDate"
                                label="Move-in Date"
                                prepend-icon="event"
                                readonly
                                v-on="on"
                                :disabled="!reservationType"
                                persistent-hint
                                :hint="!reservationType ? 'Select a reservation type' : ''"
                            ></v-text-field>
                        </template>
                        <v-date-picker
                            data-testid="reservation-move-in-date-picker"
                            class="reservation-move-in-date-picker"
                            v-model="moveInDate"
                            no-title
                            scrollable
                            :min="today"
                            :max="reservationType == 'inquiry' ? null : reservationMax"
                            @change="$refs.moveIn.save(moveInDate)"
                        />
                    </v-menu>
                </v-flex>
                <v-flex xs6 class="pl-1 pr-2">
                    <v-menu
                        data-testid="reservation-follow-up-menu"
                        class="reservation-follow-up-menu"
                        ref="followUp"
                        v-model="followUpSelect"
                        :close-on-content-click="false"
                        :return-value.sync="followupDate"
                        :nudge-right="10"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                    >
                        <template v-slot:activator="{ on }">
                            <app-input
                                data-testid="reservation-follow-up-input"
                                class="reservation-follow-up-input"
                                filled
                                v-model="followupDate"
                                label="Follow-up Date"
                                prepend-icon="event"
                                readonly
                                v-on="on"
                                :disabled="!reservationType"
                                persistent-hint
                                :hint="!reservationType ? 'Select a reservation type' : ''"
                            ></app-input>
                        </template>
                        <v-date-picker
                            data-testid="reservation-followup-date-picker"
                            class="reservation-followup-date-picker"
                            v-model="followupDate"
                            no-title
                            scrollable
                            :min="today"
                            :max="reservationType == 'inquiry' ? null : reservationMax"
                            @change="$refs.followUp.save(followupDate)"
                        />
                    </v-menu>
                </v-flex>
            </v-layout>
        </v-form>
    </v-layout>
</template>

<script>
import FacilityTenantSearch from "@/views/facilities/partials/FacilityTenantSearch";
import moment from "moment";

export default {
  props: ["tenant"],

  data() {
    return {
      moveInSelect: null,
      followUpSelect: null
    };
  },

  methods: {
    handleResetForm() {
      this.$store.dispatch("handleResetTenant");
    },

    setTenant(tenant) {
      const payload = {
        first_name: tenant.first_name,
        last_name: tenant.last_name,
        email: tenant.email,
        phone_number: this.addDashes(tenant.phone_number),
        external_id: tenant.pms_tenant_id ? tenant.pms_tenant_id : null
      };
      this.$store.commit("updateInfo", payload);
    },

    addDashes(phone) {
      if (phone) {
        if (phone.length === 10 && typeof phone.replace === "function") {
          return phone.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
        } else {
          return phone;
        }
      }
    }
  },

  computed: {
    today() {
      let now = new Date();
      return new moment(now).format("YYYY-MM-DD");
    },
    reservationMax() {
      let date = new Date();
      return new moment(date).add(this.$store.state.tenant.reservationMax, "days").format("YYYY-MM-DD");
    },
    firstName: {
      get() {
        return this.$store.state.tenant.info.first_name;
      },
      set(newVal) {
        this.$store.commit("updateFirstName", newVal);
      }
    },
    lastName: {
      get() {
        return this.$store.state.tenant.info.last_name;
      },
      set(newVal) {
        this.$store.commit("updateLastName", newVal);
      }
    },
    phone: {
      get() {
        return this.$store.state.tenant.info.phone_number;
      },
      set(newVal) {
        this.$store.commit("updatePhone", newVal);
      }
    },
    email: {
      get() {
        return this.$store.state.tenant.info.email;
      },
      set(newVal) {
        this.$store.commit("updateEmail", newVal);
      }
    },
    reservationType: {
      get() {
        return this.$store.state.tenant.options.reservationType;
      },
      set(newVal) {
        this.$store.commit("updateReservationType", newVal);
      }
    },
    followupDate: {
      get() {
        return this.$store.state.tenant.options.followupDate;
      },
      set(newVal) {
        this.$store.commit("updateFollowupDate", newVal);
      }
    },
    moveInDate: {
      get() {
        return this.$store.state.tenant.options.moveInDate;
      },
      set(newVal) {
        this.$store.commit("updateMoveInDate", newVal);
      }
    },
    darkMode() {
      return this.$store.state.application.settings.darkTheme;
    }
  },

  components: {
    FacilityTenantSearch
  }
};
</script>
